// cart-shipping-protection.js
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

class CartShippingProtection {
  constructor() {
    this.variantList = [];
    this.toggleSelector = 'input.switch[name="attributes[shipping-protection]"]';

    this.onAnyChangeCapture = this.onAnyChangeCapture.bind(this);
    this.onThemeCartUpdate = this.onThemeCartUpdate.bind(this);
    this.onSectionLoad = this.onSectionLoad.bind(this);

    this.bootstrap();
  }

  bootstrap() {
    // Capture-phase listener + composedPath() to catch events from injected DOM and open shadow roots
    document.addEventListener('change', this.onAnyChangeCapture, true);
    document.addEventListener(ThemeEvents.cartUpdate, this.onThemeCartUpdate);

    // Section lifecycle (Shopify morph)
    document.addEventListener('shopify:section:load', this.onSectionLoad);
    document.addEventListener('shopify:section:unload', this.onSectionLoad);

    // First scan
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.rescan(), { once: true });
    } else {
      this.rescan();
    }

    // Mutation observer to rescan when cart UI is injected/replaced
    this.mo = new MutationObserver(() => queueMicrotask(() => this.rescan()));
    this.mo.observe(document.documentElement, { childList: true, subtree: true });

    // Guard: if a session reload lands with only SP in cart, clear it
    this.checkCartAndClearIfOnlySP();
  }

  onSectionLoad() {
    this.rescan();
  }

  rescan() {
    // Liquid renders data-variantId, exposed as data-variantid in DOM; select the lowercase attribute:contentReference[oaicite:2]{index=2}
    const ids = new Set();
    document.querySelectorAll('[data-variantid]').forEach((el) => {
      const id = Number(el.getAttribute('data-variantid'));
      if (Number.isFinite(id)) ids.add(id);
    });
    this.variantList = Array.from(ids);
  }

  /**
   * Capture-phase handler that works even if the toggle lives inside injected DOM.
   * Uses composedPath() so events bubbling out of open shadow roots are handled.
   */
  onAnyChangeCapture(evt) {
    const path = typeof evt.composedPath === 'function' ? evt.composedPath() : [evt.target];
    const el = path.find(
      (n) =>
        n &&
        n.nodeType === 1 &&
        n.matches &&
        n.matches(this.toggleSelector)
    );
    if (!el) return;

    // Ensure we have variant ids even if the UI just appeared
    if (!this.variantList.length) this.rescan();

    const selectedId = Number(el.getAttribute('data-variantid'));
    if (!Number.isFinite(selectedId)) return;

    const params = new URLSearchParams();

    if (el.checked) {
      // Add selected SP, zero all others
      this.variantList.forEach((id) => params.append(`updates[${id}]`, id === selectedId ? '1' : '0'));
    } else {
      // Remove all SP variants
      this.variantList.forEach((id) => params.append(`updates[${id}]`, '0'));
    }

    this.postUpdate('/cart/update.js', params, el);
  }

  async onThemeCartUpdate(event) {
    // Your inline script keyed off ThemeEvents.cartUpdate; keep parity:contentReference[oaicite:3]{index=3}
    const cart = event?.detail?.cart ?? event?.detail?.data;
    if (!cart) return;

    // Auto-clear if SP is the only item (same behaviour you had in Liquid):contentReference[oaicite:4]{index=4}
    if (Array.isArray(cart.items) && cart.items.length === 1) {
      const only = cart.items[0];
      if (only && this.variantList.includes(only.id)) {
        await this.clearCart();
      }
    }

    // UI may have been re-rendered; refresh ids
    this.rescan();
  }

  async checkCartAndClearIfOnlySP() {
    try {
      const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());
      if (!this.variantList.length) this.rescan();
      if (Array.isArray(cart.items) && cart.items.length === 1) {
        const only = cart.items[0];
        if (only && this.variantList.includes(only.id)) await this.clearCart();
      }
    } catch (_) {}
  }

  async clearCart() {
    await this.postUpdate('/cart/clear.js', null, null, true);
  }

  async postUpdate(endpoint, params, triggerEl, isClear = false) {
    // Show spinner on the switch (CSS already present in your Liquid):contentReference[oaicite:5]{index=5}
    if (triggerEl) {
      triggerEl.classList.add('is-loading');
      triggerEl.disabled = true;
    }

    try {
      const fetchOpts = isClear
        ? { method: 'POST', credentials: 'same-origin' }
        : {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
            body: params?.toString() ?? '',
            credentials: 'same-origin',
          };

      const res = await fetch(endpoint, fetchOpts);
      if (!res.ok) throw new Error(`Cart update failed: ${res.status}`);

      const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());

      // Dispatch theme event so other components update (same pattern your inline code used):contentReference[oaicite:6]{index=6}
      const evt = new CartUpdateEvent(cart, 'manual-trigger', {
        itemCount: cart.item_count,
        source: 'shipping-protection',
        sections: {},
      });
      document.dispatchEvent(evt);
    } catch (err) {
      console.error(err);
    } finally {
      if (triggerEl) {
        triggerEl.classList.remove('is-loading');
        triggerEl.disabled = false;
      }
    }
  }
}

// Singleton, consistent with Horizon component style in cart icon:contentReference[oaicite:7]{index=7}
if (!window.cartShippingProtection) {
  window.cartShippingProtection = new CartShippingProtection();
}
